import { useState } from "react";
import { PageHero, Reveal, SectionEyebrow } from "@/components/Primitives";
import { IMAGES, CI_FACTORS } from "@/lib/constants";
import { postCIScore, postDemoRequest } from "@/lib/api";
import { Activity, BarChart3, Boxes, Brain, Building2, Layers, MapPin, ScanLine, ShieldCheck, Smartphone, Sparkles, Zap } from "lucide-react";
import { toast } from "sonner";

const CAPABILITIES = [
  { icon: BarChart3, title: "CI scoring", body: "Continuous cleanability scoring across portfolios, buildings, and spaces." },
  { icon: Building2, title: "Building assessment", body: "Structured audits, photo evidence and benchmarking against peers." },
  { icon: Activity, title: "Operational analytics", body: "Live dashboards on labour, chemical use, throughput and outcomes." },
  { icon: Brain, title: "Predictive maintenance", body: "Forecasts of failure, fatigue and friction before they hit the floor." },
  { icon: ScanLine, title: "Space scanning", body: "Vision-based audits for surfaces, geometries and accessibility." },
  { icon: ShieldCheck, title: "Hygiene intelligence", body: "Pathogen risk, ventilation and chemistry overlays for sensitive environments." },
  { icon: Layers, title: "Digital twins", body: "Cleanability twins that mirror real operations in real time." },
  { icon: Smartphone, title: "Mobile integration", body: "Field-grade apps for supervisors, auditors and operators." },
];

export default function Platform() {
  const [values, setValues] = useState(Object.fromEntries(CI_FACTORS.map((f) => [f.key, 6])));
  const [result, setResult] = useState(null);
  const [scoring, setScoring] = useState(false);
  const [demo, setDemo] = useState({ name: "", email: "", organisation: "", role: "", sector: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const compute = async () => {
    setScoring(true);
    try {
      const { data } = await postCIScore(values);
      setResult(data);
    } catch {
      toast.error("Unable to compute score.");
    } finally {
      setScoring(false);
    }
  };

  const submitDemo = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await postDemoRequest(demo);
      toast.success("Demo requested. We will be in touch within two working days.");
      setDemo({ name: "", email: "", organisation: "", role: "", sector: "", notes: "" });
    } catch {
      toast.error("Could not submit your request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="platform-page">
      {/* dark hero */}
      <section className="relative overflow-hidden bg-[#04070e] text-white">
        <div className="absolute inset-0 grid-pattern opacity-[0.12]" />
        <div className="absolute -right-40 -top-40 h-96 w-96 rounded-full bg-[hsl(var(--accent))]/20 blur-3xl" />
        <div className="container-x pt-16 md:pt-24 pb-20 md:pb-28 relative">
          <div className="flex items-center gap-3">
            <span className="h-px w-8 bg-[hsl(var(--accent))]" />
            <span className="eyebrow text-[hsl(var(--accent))]">CleanabilityIQ</span>
          </div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-7xl tracking-tight font-medium text-balance max-w-5xl">
            The operating system for the maintainable built environment.
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-white/70 leading-relaxed">
            CleanabilityIQ brings CI scoring, predictive analytics, audit tooling, digital twins and
            field-grade mobile into one institutional platform.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href="#demo" data-testid="platform-cta-demo" className="inline-flex items-center gap-2 px-5 h-12 rounded-md bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:opacity-90">
              Request a demo <Sparkles className="h-4 w-4" />
            </a>
            <a href="#ci-calculator" data-testid="platform-cta-calc" className="inline-flex items-center gap-2 px-5 h-12 rounded-md border border-white/15 text-sm font-medium text-white/90 hover:bg-white/5">
              Try the CI calculator
            </a>
          </div>

          <div className="mt-16 relative rounded-3xl border border-white/10 overflow-hidden">
            <img src={IMAGES.dashboard} alt="Dashboard" className="w-full h-auto" />
            <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-md bg-black/60 backdrop-blur px-3 py-1.5 text-xs">
              <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] pulse-dot relative" />
              412 buildings · live
            </div>
          </div>
        </div>
      </section>

      {/* capabilities */}
      <section className="container-x section-y">
        <SectionEyebrow>Capabilities</SectionEyebrow>
        <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium max-w-3xl text-balance">An institutional intelligence layer.</h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {CAPABILITIES.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.04}>
              <div data-testid={`capability-${i}`} className="rounded-2xl border border-border bg-card p-7 hover-lift h-full">
                <c.icon className="h-5 w-5 text-[hsl(var(--accent))]" />
                <div className="mt-5 font-display text-base md:text-lg font-medium tracking-tight">{c.title}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CI Calculator */}
      <section id="ci-calculator" className="border-y border-border bg-secondary/40">
        <div className="container-x section-y">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <SectionEyebrow>Calculator</SectionEyebrow>
              <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium text-balance">
                Score the cleanability of your space.
              </h2>
              <p className="mt-6 text-muted-foreground">
                A rapid five-factor estimate. Real-world CI assessments use over thirty weighted variables, validated by certified auditors.
              </p>
              <div className="mt-10 space-y-6">
                {CI_FACTORS.map((f) => (
                  <div key={f.key} data-testid={`ci-factor-${f.key}`}>
                    <div className="flex items-baseline justify-between">
                      <label className="text-sm font-medium">{f.label}</label>
                      <span className="font-display text-lg font-medium">{values[f.key]}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{f.hint}</p>
                    <input
                      type="range"
                      min="0"
                      max="10"
                      value={values[f.key]}
                      onChange={(e) => setValues({ ...values, [f.key]: Number(e.target.value) })}
                      className="w-full mt-3 accent-[hsl(var(--accent))]"
                      data-testid={`ci-slider-${f.key}`}
                    />
                  </div>
                ))}
              </div>
              <button
                onClick={compute}
                disabled={scoring}
                data-testid="ci-calculate"
                className="mt-8 inline-flex items-center gap-2 px-6 h-12 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-60"
              >
                {scoring ? "Calculating…" : "Calculate score"} <Zap className="h-4 w-4" />
              </button>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-3xl border border-border bg-card p-10 md:p-14 relative overflow-hidden">
                <div className="absolute inset-0 grid-pattern opacity-30" />
                <div className="relative">
                  <div className="font-mono-mini text-muted-foreground">Your composite score</div>
                  <div className="mt-6 flex items-end gap-6">
                    <div className="font-display text-7xl md:text-8xl lg:text-9xl font-medium tracking-tight text-[hsl(var(--accent))]" data-testid="ci-score-value">
                      {result ? result.score : "—"}
                    </div>
                    {result && (
                      <div className="mb-3">
                        <div className="font-display text-3xl font-medium">Grade {result.grade}</div>
                        <div className="text-sm text-muted-foreground">{result.label}</div>
                      </div>
                    )}
                  </div>
                  <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {CI_FACTORS.map((f) => (
                      <div key={f.key} className="rounded-xl border border-border bg-secondary/40 p-4">
                        <div className="font-mono-mini text-muted-foreground text-[10px]">{f.label}</div>
                        <div className="mt-1 font-display text-xl font-medium">{values[f.key]}<span className="text-muted-foreground text-sm">/10</span></div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-8 text-xs text-muted-foreground">
                    Composite scores are indicative. A full CleanabilityIQ assessment includes site visits, materials sampling, and labour audits.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* demo form */}
      <section id="demo" className="container-x section-y">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionEyebrow>Request a demo</SectionEyebrow>
            <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium text-balance">
              See CleanabilityIQ inside your portfolio.
            </h2>
            <p className="mt-6 text-muted-foreground">A senior team member will be in touch within two working days.</p>
            <div className="mt-10 grid grid-cols-2 gap-3">
              {[
                ["Dashboards", BarChart3],
                ["Twin model", Boxes],
                ["Field audits", Smartphone],
                ["Standards", ShieldCheck],
              ].map(([l, Icon], i) => (
                <div key={l} className="rounded-xl border border-border bg-card p-4 flex items-center gap-3">
                  <Icon className="h-4 w-4 text-[hsl(var(--accent))]" />
                  <span className="text-sm">{l}</span>
                </div>
              ))}
            </div>
          </div>
          <form onSubmit={submitDemo} data-testid="demo-form" className="lg:col-span-7 rounded-3xl border border-border bg-card p-8 md:p-10 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Input label="Full name" value={demo.name} onChange={(v) => setDemo({ ...demo, name: v })} testId="demo-name" required />
              <Input label="Work email" type="email" value={demo.email} onChange={(v) => setDemo({ ...demo, email: v })} testId="demo-email" required />
              <Input label="Organisation" value={demo.organisation} onChange={(v) => setDemo({ ...demo, organisation: v })} testId="demo-org" required />
              <Input label="Your role" value={demo.role} onChange={(v) => setDemo({ ...demo, role: v })} testId="demo-role" />
              <Input label="Sector" value={demo.sector} onChange={(v) => setDemo({ ...demo, sector: v })} testId="demo-sector" />
            </div>
            <div>
              <label className="text-xs font-mono-mini text-muted-foreground">Anything we should know?</label>
              <textarea
                data-testid="demo-notes"
                value={demo.notes}
                onChange={(e) => setDemo({ ...demo, notes: e.target.value })}
                rows={4}
                className="mt-2 w-full px-4 py-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent))]"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="demo-submit"
              className="inline-flex items-center gap-2 px-6 h-12 rounded-md bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:opacity-90 disabled:opacity-60"
            >
              {submitting ? "Submitting…" : "Request demo"} <MapPin className="h-4 w-4" />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testId, required }) {
  return (
    <label className="block">
      <div className="text-xs font-mono-mini text-muted-foreground">{label}</div>
      <input
        data-testid={testId}
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full h-11 px-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent))]"
      />
    </label>
  );
}
